import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, Button } from '@mui/material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
// routes
import { PATH } from '../../../routes/paths';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components-custom/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
import LanguagePopover from './LanguagePopover';
import ActivityPopover from './ActivityPopover';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const isDesktop = useResponsive('up', 'lg');

  const { translate } = useLocales();
  const { user } = useAuth();

  const renderSubscriptionPayment = () => {
    if (user?.business && user?.business?.subscription_payment) {
      if (user.business.subscription_payment.trial) {
        if (user.business.subscription_payment.need_payment) {
          return (
            <Button
              color="error"
              component={RouterLink}
              to={PATH.management.subscription.root}
              disabled={!user?.business?.is_owner}
            >
              {translate('layout.navbar.subscriptionPayment.trial.need_payment')}
            </Button>
          );
        }
        return (
          <Button
            color="warning"
            component={RouterLink}
            to={PATH.management.subscription.root}
            disabled={!user?.business?.is_owner}
          >
            {translate(
              `layout.navbar.subscriptionPayment.trial.${
                user.business.subscription_payment.days > 2 ? 3 : user.business.subscription_payment.days
              }`,
              {
                days: user.business.subscription_payment.days,
              }
            )}
          </Button>
        );
      }
      if (user.business.subscription_payment.need_payment) {
        return (
          <Button
            color="error"
            component={RouterLink}
            to={PATH.management.subscription.root}
            disabled={!user?.business?.is_owner}
          >
            {translate('layout.navbar.subscriptionPayment.need_payment')}
          </Button>
        );
      }
      if (user.business.subscription_payment.payable) {
        return (
          <Button
            color="warning"
            component={RouterLink}
            to={PATH.management.subscription.root}
            disabled={!user?.business?.is_owner}
          >
            {translate('layout.navbar.subscriptionPayment.payable')}
          </Button>
        );
      }
    }
    return <></>;
  };

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}

        {user.business && !user.employee_account && <Searchbar />}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {!window.isNativeApp && !user.employee_account && renderSubscriptionPayment()}
          <ActivityPopover />
          <LanguagePopover />
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
